import { Cart, CartItem, Order, OrderSummary, PaymentMethod } from '../types/cart.types';
import { stripeService } from './stripeService';
import { agencyService } from './agencyService';
import { voucherService } from './voucherService';
import apiClient from './apiClient';
import Packs from 'pages/Packs';

class CartService {
  // Clé de stockage local du panier
  private readonly CART_STORAGE_KEY = 'sabily_cart';

  async createVoucherByCard(paymentIntentId: string,description: string, packs: { packId: number; quantity: number;  }[]): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.post('v1/vouchers/create/card', {

            packs,
            paymentIntentId,
            description

        });
        return response.data;
  }

  /**
   * Récupère le panier actuel depuis le localStorage
   */
  getCart(): Cart {
    const cartData = localStorage.getItem(this.CART_STORAGE_KEY);
    if (!cartData) {
      // Si le panier n'existe pas, créer un nouveau
      const newCart: Cart = {
        items: [],
        totalAmount: 0,
        agencyId: this.getAgencyId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      this.saveCart(newCart);
      return newCart;
    }
    
    try {
      return JSON.parse(cartData);
    } catch (error) {
      console.error('Error parsing cart data:', error);
      // En cas d'erreur, retourner un panier vide
      const newCart: Cart = {
        items: [],
        totalAmount: 0,
        agencyId: this.getAgencyId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      this.saveCart(newCart);
      return newCart;
    }
  }

  /**
   * Enregistre le panier dans le localStorage
   */
  private saveCart(cart: Cart): void {
    localStorage.setItem(this.CART_STORAGE_KEY, JSON.stringify(cart));
  }

  /**
   * Ajoute un élément au panier
   */
  addItem(item: Omit<CartItem, 'id' | 'totalPrice'>): CartItem {
    const cart = this.getCart();
    
    // Vérifier si l'élément existe déjà dans le panier
    const existingItemIndex = cart.items.findIndex(i => i.packId === item.packId);
    
    if (existingItemIndex >= 0) {
      // Mettre à jour la quantité d'un élément existant
      cart.items[existingItemIndex].quantity += item.quantity;
      cart.items[existingItemIndex].totalPrice = cart.items[existingItemIndex].quantity * cart.items[existingItemIndex].unitPrice;
      
      this.updateCartTotal(cart);
      this.saveCart(cart);
      
      return cart.items[existingItemIndex];
    } else {
      // Ajouter un nouvel élément
      const newItem: CartItem = {
        ...item,
        id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Générer un ID unique
        totalPrice: item.quantity * item.unitPrice
      };
      
      cart.items.push(newItem);
      this.updateCartTotal(cart);
      this.saveCart(cart);
      
      return newItem;
    }
  }

  /**
   * Met à jour la quantité d'un élément dans le panier
   */
  updateItemQuantity(itemId: string, quantity: number): Cart {
    const cart = this.getCart();
    
    const itemIndex = cart.items.findIndex(i => i.id === itemId);
    if (itemIndex >= 0) {
      if (quantity <= 0) {
        // Si la quantité est 0 ou négative, supprimer l'élément
        return this.removeItem(itemId);
      }
      
      cart.items[itemIndex].quantity = quantity;
      cart.items[itemIndex].totalPrice = quantity * cart.items[itemIndex].unitPrice;
      
      this.updateCartTotal(cart);
      this.saveCart(cart);
    }
    
    return cart;
  }

  /**
   * Supprime un élément du panier
   */
  removeItem(itemId: string): Cart {
    const cart = this.getCart();
    
    cart.items = cart.items.filter(i => i.id !== itemId);
    
    this.updateCartTotal(cart);
    this.saveCart(cart);
    
    return cart;
  }

  /**
   * Vide le panier
   */
  clearCart(): Cart {
    const emptyCart: Cart = {
      items: [],
      totalAmount: 0,
      agencyId: this.getAgencyId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    this.saveCart(emptyCart);
    return emptyCart;
  }

  /**
   * Met à jour le montant total du panier
   */
  private updateCartTotal(cart: Cart): void {
    cart.totalAmount = cart.items.reduce((sum, item) => sum + item.totalPrice, 0);
    cart.updatedAt = new Date().toISOString();
  }

  /**
   * Récupère l'ID de l'agence depuis localStorage
   */
  private getAgencyId(): number {
    const userStr = localStorage.getItem('user');
    if (!userStr) return 0;
    
    try {
      const user = JSON.parse(userStr);
      return user.agencyId || 0;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return 0;
    }
  }
  /**
   * Convertit le panier actuel en commande
   * @param paymentMethod Méthode de paiement
   * @param paymentIntentId ID de l'intention de paiement Stripe (optionnel)
   */
  async checkout(paymentMethod: PaymentMethod, paymentIntentId?: string): Promise<Order> {
    const cart = this.getCart();
    
    if (cart.items.length === 0) {
      throw new Error('Le panier est vide');
    }

    // Gérer le paiement par solde
    if (paymentMethod === 'BALANCE') {
      return this.processBalancePayment(cart);
    }
    
    // Dans une implémentation réelle, cela ferait un appel API
    try {
      // Lorsque l'API sera disponible, nous ferons un appel POST ici
      // const response = await apiClient.post<Order>('/orders', { 
      //   items: cart.items, 
      //   paymentMethod,
      //   paymentIntentId
      // });
      
      // Simuler une réponse pour le moment
      const order: Order = {
        id: Date.now(), // Simuler un ID
        agencyId: cart.agencyId,
        items: [...cart.items],
        totalAmount: cart.totalAmount,
        status: paymentMethod === 'CARD' && !paymentIntentId ? 'PENDING' : 'COMPLETED',
        paymentMethod,
        paymentStatus: paymentMethod === 'CARD' && !paymentIntentId ? 'PENDING' : 'COMPLETED',
        orderedAt: new Date().toISOString(),
        completedAt: paymentMethod !== 'CARD' || paymentIntentId ? new Date().toISOString() : undefined
      };
      
      // Vider le panier après la commande réussie
      this.clearCart();
      
      return order;
    } catch (error) {
      console.error('Checkout error:', error);
      throw new Error('Échec de la commande');
    }
  }

  /**
   * Traite un paiement par solde
   * Vérifie le solde, débite le compte et crée les vouchers
   */
  private async processBalancePayment(cart: Cart): Promise<Order> {
    try {      // 1. Vérifier si l'agence a suffisamment de solde
      const balanceCheckResponse = await agencyService.checkSufficientBalance(
        cart.agencyId,
        cart.totalAmount
      );
      console.log('Balance check response:', balanceCheckResponse);
      if (!balanceCheckResponse.sufficient) {
        throw new Error(`Solde insuffisant. Solde disponible: ${balanceCheckResponse.currentBalance}€, Montant requis: ${cart.totalAmount}€`);
      }

      // 2. Créer la commande
      const order: Order = {
        id: Date.now(), // Simuler un ID - sera remplacé par l'ID de l'API
        agencyId: cart.agencyId,
        items: [...cart.items],
        totalAmount: cart.totalAmount,
        status: 'PROCESSING',
        paymentMethod: 'BALANCE',
        paymentStatus: 'PENDING',
        orderedAt: new Date().toISOString(),
        voucherIds: []
      };
/* todo
      // 3. Débiter le solde de l'agence
      await agencyService.debitBalance(
        cart.agencyId, 
        cart.totalAmount, 
        order.id,
        `Paiement par solde - Commande #${order.id}`
      );
*/
      // 4. Créer les vouchers pour chaque ligne de commande
      const voucherIds: number[] = [];
      const packs = cart.items.map(item => ({
        packId: item.packId,
        quantity: item.quantity
      }));
      try {
            

            const voucherResponse = await voucherService.generateForAgency(({
                packs,
                description: `Voucher pour ${cart.items.map(item => item.packName).join(', ')} - Commande #${order.id}`
              }));
            voucherIds.push(voucherResponse.data);
          } catch (voucherError) {
            console.error(`Erreur lors de la création du voucher pour la commande ${order.id}:`, voucherError);
            // Continue avec les autres vouchers même si un échoue
          }
      // 5. Mettre à jour la commande avec les IDs des vouchers créés
      order.voucherIds = voucherIds;
      order.status = 'COMPLETED';
      order.paymentStatus = 'COMPLETED';
      order.completedAt = new Date().toISOString();

      // 6. Vider le panier après la commande réussie
      this.clearCart();

      return order;    } catch (error) {
      console.error('Erreur lors du paiement par solde:', error);
        // Gestion des erreurs API non implémentées (fallback)
      if ((error as any).response?.status === 404 || (error as any).code === 'ECONNREFUSED') {
        console.warn('API de paiement par solde non disponible - utilisation du mode simulation');
        
        // Mode simulation pour le développement
        const simulatedOrder: Order = {
          id: Date.now(),
          agencyId: cart.agencyId,
          items: [...cart.items],
          totalAmount: cart.totalAmount,
          status: 'COMPLETED',
          paymentMethod: 'BALANCE',
          paymentStatus: 'COMPLETED',
          orderedAt: new Date().toISOString(),
          completedAt: new Date().toISOString(),
          voucherIds: cart.items.map((_, index) => Date.now() + index) // IDs simulés
        };
        
        this.clearCart();
        return simulatedOrder;
      }
      
      throw new Error(error instanceof Error ? error.message : 'Échec du paiement par solde');
    }
  }

  

  /**
   * Vérifie si une agence a suffisamment de solde pour une commande
   */
  async checkBalance(amount?: number): Promise<{ sufficient: boolean; currentBalance: number; requiredAmount: number }> {
    const cart = this.getCart();
    const requiredAmount = amount || cart.totalAmount;
      try {
      const response = await agencyService.checkSufficientBalance(cart.agencyId, requiredAmount);
      console.log('Balance check response:', response);
      return {
        sufficient: response.sufficient,
        currentBalance: response.currentBalance,
        requiredAmount
      };    } catch (error) {
      console.error('Erreur lors de la vérification du solde:', error);
        // Fallback pour le développement si l'API n'est pas disponible
      if ((error as any).response?.status === 404 || (error as any).code === 'ECONNREFUSED') {
        console.warn('API de vérification de solde non disponible - simulation avec solde de 1000€');
        return {
          sufficient: requiredAmount <= 1000, // Simuler un solde de 1000€
          currentBalance: 1000,
          requiredAmount
        };
      }
      
      return {
        sufficient: false,
        currentBalance: 0,
        requiredAmount
      };
    }
  }

  /**
   * Crée une session de paiement Stripe
   */
  async createStripeSession(successUrl: string, cancelUrl: string): Promise<string> {
    const cart = this.getCart();
    
    if (cart.items.length === 0) {
      throw new Error('Le panier est vide');
    }

    try {
      const sessionData = await stripeService.createPaymentSession(
        cart.items.map(item => ({
          id: item.id,
          packName: item.packName,
          quantity: item.quantity,
          unitPrice: item.unitPrice
        })),
        cart.totalAmount,
        successUrl,
        cancelUrl
      );

      return sessionData.sessionId;
    } catch (error) {
      console.error('Error creating Stripe session:', error);
      throw new Error('Échec de la création de la session de paiement');
    }
  }

  /**
   * Traite un paiement par carte de crédit via Stripe
   */
  async processCardPayment(paymentIntentId: string): Promise<Order> {
    try {
      // Ici, on finaliserait le paiement côté serveur
      // Pour l'instant, on passe directement à checkout avec l'identifiant du paiement
      return this.checkout('CARD', paymentIntentId);
    } catch (error) {
      console.error('Error processing card payment:', error);
      throw new Error('Échec du traitement du paiement par carte');
    }
  }

  /**
   * Récupère les commandes d'une agence
   * Cette méthode sera implémentée côté API plus tard
   */
  async getOrders(): Promise<OrderSummary[]> {
    // Dans une implémentation réelle, cela ferait un appel API
    // Simuler des données pour le moment
    // const response = await apiClient.get<OrderSummary[]>('/orders');
    // return response.data;
    
    return [
      {
        id: 1001,
        totalAmount: 1250,
        itemCount: 3,
        status: 'COMPLETED',
        paymentMethod: 'CARD',
        orderedAt: '2025-05-10T10:30:00Z'
      },
      {
        id: 1002,
        totalAmount: 850,
        itemCount: 2,
        status: 'PENDING',
        paymentMethod: 'CASH',
        orderedAt: '2025-05-11T14:45:00Z'
      }
    ];
  }

  /**
   * Récupère les détails d'une commande
   * Cette méthode sera implémentée côté API plus tard
   */
  async getOrder(orderId: number): Promise<Order | null> {
    // Dans une implémentation réelle, cela ferait un appel API
    // const response = await apiClient.get<Order>(`/orders/${orderId}`);
    // return response.data;
    
    // Pour le moment, retourner null
    return null;
  }
}

export const cartService = new CartService();
